import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { currentTenant, getAccessToken } from "@/auth/session";

/** Which cached screens each live event makes stale. */
const STALE: Record<string, string[][]> = {
  "approval.decided": [
    ["needs-you"],
    ["approval"],
    ["overview"],
    ["me"],
    ["cases"],
    ["case"],
    ["autonomy"],
    ["audit"],
    ["flow-runs"],
  ],
  "case.updated": [["cases"], ["case"], ["overview"]],
  "approval.created": [["needs-you"], ["overview"], ["me"], ["cases"], ["case"], ["flow-runs"]],
  "signal.received": [["signals"], ["connections"]],
  "incident.updated": [["case"], ["cases"], ["signals"]],
  "run.updated": [
    ["cases"],
    ["case"],
    ["overview"],
    ["flows"],
    ["flow"],
    ["crew"],
    ["audit"],
    ["autonomy"],
    ["flow-runs"],
  ],
  "flow.changed": [["flows"], ["flow"], ["autonomy"], ["audit"], ["flow-test-runs"]],
  "connector.changed": [
    ["connectors"],
    ["connector"],
    ["flow-palette"],
    ["connections"],
    // The footer shows ServiceNow: where tickets go, a pause, Allow changes off.
    ["status"],
    ["audit"],
  ],
  "policy.changed": [["autonomy"], ["flows"], ["flow"], ["audit"]],
  "rule.changed": [["autonomy"], ["audit"]],
  "hold.changed": [["status"], ["autonomy"], ["audit"], ["notifications"]],
  "agent.changed": [["agent"], ["crew"], ["overview"], ["audit"]],
  "guardian.changed": [["guardian"], ["audit"]],
  "notifications.changed": [["notifications"], ["audit"]],
  "war_room.updated": [["war-room"], ["audit"]],
  "war_room.closed": [["war-room"], ["war-rooms"], ["cases"], ["case"], ["audit"]],
  "estate.changed": [["estate"], ["case"], ["audit"]],
  "estate.readings": [["estate"]],
  "knowledge.changed": [
    ["knowledge"],
    ["knowledge-search"],
    ["knowledge-drafts"],
    ["knowledge-doc"],
    ["case-knowledge"],
    ["audit"],
  ],
};

export type LiveState = "connecting" | "live" | "offline";

/** One event from the server, as the pulse line and other live drawings see it. */
export interface LiveEvent {
  kind: string;
  at: string;
  data: Record<string, unknown>;
}

const listeners = new Set<(e: LiveEvent) => void>();

/** Hear every live event; returns the function that stops listening. */
export function subscribeLive(listener: (e: LiveEvent) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Keeps one WebSocket open and refreshes whatever a live event changes. */
export function useLiveEvents(): LiveState {
  const qc = useQueryClient();
  const [state, setState] = useState<LiveState>("connecting");

  useEffect(() => {
    let socket: WebSocket | null = null;
    let retry = 1000;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let closed = false;

    const connect = async () => {
      const token = await getAccessToken();
      if (closed) return;
      const proto = window.location.protocol === "https:" ? "wss" : "ws";
      const tenant = currentTenant();
      const url =
        `${proto}://${window.location.host}/api/v1/stream?token=${encodeURIComponent(token ?? "")}` +
        (tenant ? `&tenant=${encodeURIComponent(tenant)}` : "");
      socket = new WebSocket(url);
      socket.onopen = () => {
        retry = 1000;
        setState("live");
      };
      socket.onmessage = (msg: MessageEvent<string>) => {
        let event: LiveEvent;
        try {
          const raw = JSON.parse(msg.data) as Partial<LiveEvent>;
          event = {
            kind: raw.kind ?? "",
            at: raw.at ?? new Date().toISOString(),
            data: raw.data ?? {},
          };
        } catch {
          return;
        }
        for (const key of STALE[event.kind] ?? []) void qc.invalidateQueries({ queryKey: key });
        for (const listener of listeners) listener(event);
      };
      socket.onclose = () => {
        setState("offline");
        if (closed) return;
        timer = setTimeout(() => void connect(), retry);
        retry = Math.min(retry * 2, 30_000);
      };
    };

    void connect();
    return () => {
      closed = true;
      if (timer) clearTimeout(timer);
      socket?.close();
    };
  }, [qc]);

  return state;
}
